import React, { useState, useEffect } from 'react';
import { compressFiles, optimizeFormData, validateFormData } from '../../../shared/utils/performanceUtils';
import { useTitle } from '../../../context/TitleContext';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { FaSave, FaImage, FaGlobe, FaFacebook, FaInstagram, FaTwitter, FaYoutube, FaLinkedin, FaTiktok, FaWhatsapp } from 'react-icons/fa';
import { showSuccessToast, showErrorToast, showWarningToast, showInfoToast } from '../../../shared/utils/toastHelper';
import { fetchPharmacySettings, savePharmacySettings, clearError, clearSuccess } from '../../../redux/Slices/AdminPharmacySettingsSlice';

const AdminPharmacySettings = () => {
    const { setTitle } = useTitle();
    const dispatch = useDispatch();
    
    const { settings, loading, saving, error, success } = useSelector((state) => state.adminPharmacySettings);
    
    const [formData, setFormData] = useState({
        name_en: '',
        name_ar: '',
        tagline_en: '',
        tagline_ar: '',
        description_en: '',
        description_ar: '',
        facebook_link: '',
        instagram_link: '',
        twitter_link: '',
        youtube_link: '',
        linkedin_link: '',
        tiktok_link: '',
        whatsapp_link: '',
        website_link: '',
    });

    const [logoFile, setLogoFile] = useState(null);
    const [coverFile, setCoverFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [coverPreview, setCoverPreview] = useState(null);

    useEffect(() => {
        setTitle('إعدادات الصيدليات');
        dispatch(fetchPharmacySettings());
    }, [setTitle, dispatch]);

    useEffect(() => {
        if (settings) {
            setFormData({
                name_en: settings.name?.en || '',
                name_ar: settings.name?.ar || '',
                tagline_en: settings.tagline?.en || '',
                tagline_ar: settings.tagline?.ar || '',
                description_en: settings.description?.en || '',
                description_ar: settings.description?.ar || '',
                facebook_link: settings.facebook_link || '',
                instagram_link: settings.instagram_link || '',
                twitter_link: settings.twitter_link || '',
                youtube_link: settings.youtube_link || '',
                linkedin_link: settings.linkedin_link || '',
                tiktok_link: settings.tiktok_link || '',
                whatsapp_link: settings.whatsapp_link || '',
                website_link: settings.website_link || '',
            });
            setLogoPreview(settings.logo);
            setCoverPreview(settings.cover);
        }
    }, [settings]);

    useEffect(() => {
        if (error) {
            showErrorToast(error);
            dispatch(clearError());
        }
    }, [error, dispatch]);

    useEffect(() => {
        if (success) {
            showSuccessToast(success);
            dispatch(clearSuccess());
        }
    }, [success, dispatch]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setLogoFile(file);
            setLogoPreview(URL.createObjectURL(file));
        }
    };

    const handleCoverChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setCoverFile(file);
            setCoverPreview(URL.createObjectURL(file));
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const data = new FormData();
        data.append('name_en', formData.name_en);
        data.append('name_ar', formData.name_ar);
        data.append('tagline_en', formData.tagline_en);
        data.append('tagline_ar', formData.tagline_ar);
        data.append('description_en', formData.description_en);
        data.append('description_ar', formData.description_ar);
        data.append('facebook_link', formData.facebook_link);
        data.append('instagram_link', formData.instagram_link);
        data.append('twitter_link', formData.twitter_link);
        data.append('youtube_link', formData.youtube_link);
        data.append('linkedin_link', formData.linkedin_link);
        data.append('tiktok_link', formData.tiktok_link);
        data.append('whatsapp_link', formData.whatsapp_link);
        data.append('website_link', formData.website_link);

        if (logoFile) {
            data.append('logo', logoFile);
        }
        if (coverFile) {
            data.append('cover', coverFile);
        }

        dispatch(savePharmacySettings(data));
    };

    if (loading) {
        return (
            <div className="main_content">
                <div className="dashboard-container">
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3 text-muted">جاري تحميل الإعدادات...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="main_content">
            <div className="dashboard-container">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="mb-0">إعدادات الصيدليات</h4>
                </div>

                <form onSubmit={handleSubmit}>
                    {/* Basic Information */}
                    <div className="card shadow-lg border-0 mb-4">
                        <div className="card-header" style={{ backgroundColor: 'rgb(24, 65, 148)', color: 'white' }}>
                            <h5 className="mb-0">المعلومات الأساسية</h5>
                        </div>
                        <div className="card-body p-4">
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-medium">الاسم (English)</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="name_en"
                                        value={formData.name_en}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-medium">الاسم (العربية)</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="name_ar"
                                        value={formData.name_ar}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-medium">الشعار (English)</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="tagline_en"
                                        value={formData.tagline_en}
                                        onChange={handleChange}
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-medium">الشعار (العربية)</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="tagline_ar"
                                        value={formData.tagline_ar}
                                        onChange={handleChange}
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-medium">الوصف (English)</label>
                                    <textarea
                                        className="form-control"
                                        name="description_en"
                                        rows="4"
                                        value={formData.description_en}
                                        onChange={handleChange}
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-medium">الوصف (العربية)</label>
                                    <textarea
                                        className="form-control"
                                        name="description_ar"
                                        rows="4"
                                        value={formData.description_ar}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Images */}
                    <div className="card shadow-lg border-0 mb-4">
                        <div className="card-header" style={{ backgroundColor: 'rgb(24, 65, 148)', color: 'white' }}>
                            <h5 className="mb-0">الصور</h5>
                        </div>
                        <div className="card-body p-4">
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-medium">
                                        <FaImage className="mx-2" />
                                        اللوجو
                                    </label>
                                    <input
                                        type="file"
                                        className="form-control"
                                        accept="image/*"
                                        onChange={handleLogoChange}
                                    />
                                    {logoPreview && (
                                        <div className="mt-3">
                                            <img
                                                src={logoPreview}
                                                alt="Logo Preview"
                                                style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'contain' }}
                                                className="border rounded"
                                            />
                                        </div>
                                    )}
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-medium">
                                        <FaImage className="mx-2" />
                                        الغلاف
                                    </label>
                                    <input
                                        type="file"
                                        className="form-control"
                                        accept="image/*"
                                        onChange={handleCoverChange}
                                    />
                                    {coverPreview && (
                                        <div className="mt-3">
                                            <img
                                                src={coverPreview}
                                                alt="Cover Preview"
                                                style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'cover' }}
                                                className="border rounded"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Social Media Links */}
                    <div className="card shadow-lg border-0 mb-4">
                        <div className="card-header" style={{ backgroundColor: 'rgb(24, 65, 148)', color: 'white' }}>
                            <h5 className="mb-0">روابط التواصل الاجتماعي</h5>
                        </div>
                        <div className="card-body p-4">
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-medium">
                                        <FaFacebook className="mx-2 text-primary" />
                                        Facebook
                                    </label>
                                    <input
                                        type="url"
                                        className="form-control"
                                        name="facebook_link"
                                        value={formData.facebook_link}
                                        onChange={handleChange}
                                        placeholder="https://facebook.com/..."
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-medium">
                                        <FaInstagram className="mx-2" style={{ color: '#E4405F' }} />
                                        Instagram
                                    </label>
                                    <input
                                        type="url"
                                        className="form-control"
                                        name="instagram_link"
                                        value={formData.instagram_link}
                                        onChange={handleChange}
                                        placeholder="https://instagram.com/..."
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-medium">
                                        <FaTwitter className="mx-2" style={{ color: '#1DA1F2' }} />
                                        Twitter
                                    </label>
                                    <input
                                        type="url"
                                        className="form-control"
                                        name="twitter_link"
                                        value={formData.twitter_link}
                                        onChange={handleChange}
                                        placeholder="https://twitter.com/..."
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-medium">
                                        <FaYoutube className="mx-2 text-danger" />
                                        YouTube
                                    </label>
                                    <input
                                        type="url"
                                        className="form-control"
                                        name="youtube_link"
                                        value={formData.youtube_link}
                                        onChange={handleChange}
                                        placeholder="https://youtube.com/..."
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-medium">
                                        <FaLinkedin className="mx-2" style={{ color: '#0077B5' }} />
                                        LinkedIn
                                    </label>
                                    <input
                                        type="url"
                                        className="form-control"
                                        name="linkedin_link"
                                        value={formData.linkedin_link}
                                        onChange={handleChange}
                                        placeholder="https://linkedin.com/..."
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-medium">
                                        <FaTiktok className="mx-2" />
                                        TikTok
                                    </label>
                                    <input
                                        type="url"
                                        className="form-control"
                                        name="tiktok_link"
                                        value={formData.tiktok_link}
                                        onChange={handleChange}
                                        placeholder="https://tiktok.com/..."
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-medium">
                                        <FaWhatsapp className="mx-2 text-success" />
                                        WhatsApp
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="whatsapp_link"
                                        value={formData.whatsapp_link}
                                        onChange={handleChange}
                                        placeholder="https://wa.me/..."
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-medium">
                                        <FaGlobe className="mx-2 text-info" />
                                        Website
                                    </label>
                                    <input
                                        type="url"
                                        className="form-control"
                                        name="website_link"
                                        value={formData.website_link}
                                        onChange={handleChange}
                                        placeholder="https://www.example.com"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div className="text-center">
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            type="submit"
                            className="btn text-white px-4 py-2"
                            style={{ backgroundColor: 'rgb(24, 65, 148)', fontSize: '0.95rem', borderRadius: '0.65rem' }}
                            disabled={saving}>
                            {saving ? (
                                <>
                                    <span className="spinner-border spinner-border-sm mx-2" role="status" aria-hidden="true"></span>
                                    جاري الحفظ...
                                </>
                            ) : (
                                <>
                                    <FaSave className="mx-2" />
                                    حفظ الإعدادات
                                </>
                            )}
                        </motion.button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AdminPharmacySettings;

